import React, { useEffect, useState, useMemo } from 'react';
import { effectiveSegment } from '../../utils/sizeOrdersAs';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert as MuiAlert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Grid,
  Checkbox,
  TextField,
  Collapse,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import PriceAlertDialog from './PriceAlertDialog';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import ScienceIcon from '@mui/icons-material/Science';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewHeadlineOutlinedIcon from '@mui/icons-material/ViewHeadlineOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ClearIcon from '@mui/icons-material/Clear';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '../../contexts/TabsContext';
import { getGlobalSocket } from '../../hooks/useWebSocket';
import { tradingService } from '../../services/trading.service';
import { usePersistedState } from '../../hooks/usePersistedState';
import { chartOpenQuery, tfToMinutes } from '../../utils/timeframe';
import { Alert, AlertType, AlertStatus, AlertPriority } from '../../types';
import api from '../../services/api';
import fnoService, { FnoAssessment } from '../../services/fno.service';
import FnoScoreBadge from '../fno/FnoScoreBadge';
import { useAppTheme } from '../../hooks/useAppTheme';
import { formatPrice } from '../../utils/formatters';
import { resolveDecimals, resolveDecimalsFor } from '../../utils/decimalPrecision';
import { CH_TYPE, chNumeric } from '../../styles/appUi';
import RenotifyCountdown from '../common/RenotifyCountdown';

const DENSITY_KEY = 'alerts.density.v1';

const AlertsList: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { openChartTab } = useTabs();
  const { C, isDark } = useAppTheme();
  // R83 — default to 'compact' for the new dense row layout. Power users can
  // still flip to 'cards' (full per-alert detail) via the toggle.
  const [density, setDensity] = useState<'cards' | 'compact'>(() => {
    try { return (localStorage.getItem(DENSITY_KEY) as 'cards' | 'compact') || 'compact'; } catch { return 'compact'; }
  });
  const [executingId, setExecutingId] = useState<number | null>(null);
  // R83 — id of the alert whose compact row is currently expanded (inline
  // detail panel). Null means all rows collapsed. Only one row open at a time.
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const toggleDensity = () => {
    const next = density === 'cards' ? 'compact' : 'cards';
    setDensity(next);
    try { localStorage.setItem(DENSITY_KEY, next); } catch { /* ignore */ }
  };

  const handleQuickExecute = async (alert: Alert) => {
    const symbol = (alert as any).instrument?.symbol || (alert.parsedData as any)?.symbol || alert.symbol || '';
    const action = (alert.parsedData?.action || '').toUpperCase();
    const ok = await confirm({
      title: `Execute ${action || 'alert'} for ${symbol || 'this alert'}?`,
      body: 'A live order will be placed via the standard execution pipeline. Settings (paper trade, kill switch, order confirmation) still apply.',
      confirmLabel: 'Execute',
      confirmColor: action === 'SHORT' ? 'error' : 'success',
    });
    if (!ok) return;
    try {
      setExecutingId(alert.id);
      toast.loading(`Executing ${symbol || 'alert'}…`, { id: `aexec-${alert.id}` });
      await tradingService.executeAlert(alert.id);
      toast.success(`Executed ${symbol || 'alert'}`, { id: `aexec-${alert.id}` });
      handleRefresh();
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to execute';
      toast.error(`Execute failed: ${msg}`, { id: `aexec-${alert.id}` });
    } finally {
      setExecutingId(null);
    }
  };

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [priceAlertOpen, setPriceAlertOpen] = useState(false);
  const [fnoAssessments, setFnoAssessments] = useState<Record<number, FnoAssessment>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // R118 — live append on WS alert:new so the page stays current without a
  // manual refresh. Uses the shared global socket (same one useWebSocket
  // opens on app boot). Existing filters / sort / scroll position are
  // preserved; the new row simply appears at the top.
  useEffect(() => {
    let detached = false;
    let pollTimer: number | null = null;
    let attachedSock: ReturnType<typeof getGlobalSocket> = null;

    const onAlertNew = (data: any) => {
      if (detached) return;
      const a = data?.alert;
      if (!a || a.id == null) return;
      setAlerts(prev => {
        if (prev.some(x => x.id === a.id)) return prev; // dedup
        return [a as Alert, ...prev];
      });
    };

    const attach = () => {
      const sock = getGlobalSocket();
      if (!sock) {
        pollTimer = window.setTimeout(attach, 300);
        return;
      }
      attachedSock = sock;
      sock.on('alert:new', onAlertNew);
    };
    attach();

    return () => {
      detached = true;
      if (pollTimer != null) window.clearTimeout(pollTimer);
      if (attachedSock) attachedSock.off('alert:new', onAlertNew);
    };
  }, []);

  // Filters
  // R118 — replaced server-side typeFilter (PRICE/TECHNICAL/...) with a
  // client-side actionFilter (BUY/SHORT/SELL/COVER/SELL_FLAT/...) that
  // actually reflects what the user wants to filter on.
  // Filters — persisted so they survive navigating away and back (issue #2).
  const [actionFilter, setActionFilter] = usePersistedState<string>('alerts.actionFilter', 'all');
  // Issue 11: Priority removed from filters
  const [statusFilter, setStatusFilter] = usePersistedState<string>('alerts.statusFilter', 'all');
  const [symbolSearch, setSymbolSearch] = usePersistedState<string>('alerts.symbolSearch', '');
  // Interval chip filter: 'all' | '1' | '5' | '15' | '60' | '240' | 'D' | 'W'
  const [intervalFilter, setIntervalFilter] = usePersistedState<string>('alerts.intervalFilter', 'all');
  // R118 — Alert Origin filter: 'all' | 'TV' | 'SCAN'
  const [originFilter, setOriginFilter] = usePersistedState<string>('alerts.originFilter', 'all');
  // Strategy filter — free-text substring match (type to filter as you go).
  // Empty = no filter. New key so old 'all' dropdown value is not reused.
  const [strategyFilter, setStrategyFilter] = usePersistedState<string>('alerts.strategyText', '');
  // Signal strength filter: 'all' | 'H' | 'M' | 'L'
  const [strengthFilter, setStrengthFilter] = usePersistedState<string>('alerts.strengthFilter', 'all');
  // R118 — Date filter — single calendar day in IST format YYYY-MM-DD; empty = no filter
  const [dateFilter, setDateFilter] = usePersistedState<string>('alerts.dateFilter', '');
  // How many past days of alerts to LOAD from the server (server-side window).
  // null = no day window (loads the most recent up to the high row cap below).
  // Default 1d so the page stays responsive; user can widen via the Load chips.
  const [daysFilter, setDaysFilter] = usePersistedState<number | null>('alerts.loadDays', 1);
  // Issue 13: dedupe-then-filter — apply filters on the latest alert per symbol
  const [latestPerSymbol, setLatestPerSymbol] = useState<boolean>(false);

  // Composite client-side filter chain (Action / Origin / Strategy / Interval /
  // Strength / Date), with optional latest-per-symbol dedup applied first.
  // Memoised at component scope so the list AND the footer count both read the
  // SAME displayed set — the footer was previously showing the raw LOADED count
  // (e.g. "58") while the list showed only the filtered rows (e.g. 10).
  const filteredAlerts = useMemo<Alert[]>(() => {
    const up = (v: any) => String(v ?? '').toUpperCase();
    const matchesAction = (a: Alert) => actionFilter === 'all' || up((a.parsedData as any)?.action) === actionFilter.toUpperCase();
    const matchesOrigin = (a: Alert) => { if (originFilter === 'all') return true; const isTV = !!a.rawAlert; return originFilter === 'TV' ? isTV : !isTV; };
    const matchesStrategy = (a: Alert) => { const q = strategyFilter.trim().toUpperCase(); if (!q) return true; return up((a.parsedData as any)?.strategy || (a.parsedData as any)?.signalSource).includes(q); };
    const matchesInterval = (a: Alert) => {
      if (intervalFilter === 'all') return true;
      // Normalise both the filter ('D'/'W'/'60'…) and the stored timeframe (in-house
      // scans use minutes e.g. 1440; TradingView uses resolutions) to minutes so the
      // daily/weekly filters match regardless of how the alert recorded its timeframe.
      const want = tfToMinutes(intervalFilter);
      const have = tfToMinutes((a.parsedData as any)?.timeframe);
      return want != null && have != null && want === have;
    };
    const strengthTierOf = (a: Alert): 'H' | 'M' | 'L' | null => {
      const raw = (a as any).strength ?? (a.parsedData as any)?.strength ?? (a.parsedData as any)?.signalStrength;
      if (raw == null || raw === '') return null;
      if (typeof raw === 'number') return raw >= 3 ? 'H' : raw >= 2 ? 'M' : 'L';
      const u = up(raw); return u.startsWith('H') ? 'H' : u.startsWith('M') ? 'M' : u.startsWith('L') ? 'L' : null;
    };
    const matchesStrength = (a: Alert) => strengthFilter === 'all' || strengthTierOf(a) === strengthFilter;
    const matchesDate = (a: Alert) => {
      if (!dateFilter) return true;
      const t = new Date((a as any).triggeredAt || (a as any).createdAt || 0);
      if (!Number.isFinite(t.getTime())) return false;
      return t.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) === dateFilter;
    };
    let pool: Alert[] = alerts;
    if (latestPerSymbol) {
      const seen = new Map<string, Alert>();
      for (const a of alerts) {
        const sym = up((a.parsedData as any)?.ticker || (a as any).symbol);
        if (!sym) continue;
        const existing = seen.get(sym);
        const aTime = new Date((a as any).triggeredAt || (a as any).createdAt || 0).getTime();
        const eTime = existing ? new Date((existing as any).triggeredAt || (existing as any).createdAt || 0).getTime() : -Infinity;
        if (!existing || aTime > eTime) seen.set(sym, a);
      }
      pool = Array.from(seen.values());
    }
    return pool.filter(a => matchesAction(a) && matchesOrigin(a) && matchesStrategy(a) && matchesInterval(a) && matchesStrength(a) && matchesDate(a));
  }, [alerts, actionFilter, originFilter, strategyFilter, intervalFilter, strengthFilter, dateFilter, latestPerSymbol]);

  // Bulk selection
  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(new Set());
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState<string>('ACKNOWLEDGED');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Test Order Placement
  const [testingOrderPlacement, setTestingOrderPlacement] = useState<number | null>(null);
  const [testOrderResult, setTestOrderResult] = useState<any>(null);
  const [openTestResultDialog, setOpenTestResultDialog] = useState(false);

  // Test Webhook Alert
  const [testWebhookOpen, setTestWebhookOpen] = useState(false);
  const [testWebhookSending, setTestWebhookSending] = useState(false);
  const [testWebhookPayload, setTestWebhookPayload] = useState({
    ticker: 'BTCUSD',
    action: 'BUY',
    price: '',
    strategy: 'Sniper',
    isl: '',
    target: '',
    entryLevel: '',
    timeframe: '15',
    confirmed: 'Y'
  });

  // Lifecycle info panel
  const [lifecycleExpanded, setLifecycleExpanded] = useState(false);

  // Ticker autocomplete for test webhook dialog (live type-ahead search)
  const [tickerOptions, setTickerOptions] = useState<{ symbol: string; exchange: string; name: string }[]>([]);
  const [tickerSearchLoading, setTickerSearchLoading] = useState(false);
  const [selectedTickerExchange, setSelectedTickerExchange] = useState<string>('');

  // Inline custom price editing
  const [editingAlertId, setEditingAlertId] = useState<number | null>(null);
  const [customEntry, setCustomEntry] = useState('');
  const [customSL, setCustomSL] = useState('');
  const [customTarget, setCustomTarget] = useState('');
  const [customRenotify, setCustomRenotify] = useState('');
  const [customQuantity, setCustomQuantity] = useState('');
  const [customOrderType, setCustomOrderType] = useState<'LIMIT' | 'MARKET'>('LIMIT');
  const [placingOrder, setPlacingOrder] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  // Issue 17: Auto-refresh every 30 seconds (server may receive new alerts
  // that the WS missed — e.g. across reconnect). The WS handler appends
  // sub-second; this poll is a belt-and-suspenders backup.
  useEffect(() => {
    const interval = setInterval(() => fetchAlerts(), 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, symbolSearch, daysFilter, dateFilter]);

  // Re-fetch when SERVER-SIDE filters change (status / symbol / date window).
  // Action / origin / strategy / interval / strength filters are client-side
  // and do not trigger a refetch.
  useEffect(() => {
    fetchAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, symbolSearch, daysFilter, dateFilter]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);

      // High row cap so we are not limited to the old 20-row page. The day-range
      // chips (and the single-date picker) further narrow this server-side.
      const params: any = { page: 1, limit: 2000 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (symbolSearch) params.symbol = symbolSearch;

      // Server-side date window. A specific picked date takes precedence (with a
      // ±1 day margin so IST/UTC boundaries are covered; the exact-IST-day client
      // filter then trims it). Otherwise the day-range chip sets a rolling window.
      if (dateFilter) {
        const d = new Date(dateFilter + 'T00:00:00');
        const from = new Date(d); from.setDate(d.getDate() - 1);
        const to = new Date(d); to.setDate(d.getDate() + 2);
        params.fromDate = from.toISOString();
        params.toDate = to.toISOString();
      } else if (daysFilter && daysFilter > 0) {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        from.setDate(from.getDate() - (daysFilter - 1));
        params.fromDate = from.toISOString();
        params.toDate = new Date().toISOString();
      }

      const response = await tradingService.getAlerts(params);

      if (response.data.success && response.data.data) {
        const loadedAlerts = response.data.data.data;
        setAlerts(loadedAlerts);
        // Load FNO assessments for all alert IDs (non-blocking)
        const ids = (loadedAlerts as Alert[]).map(a => a.id).filter(Boolean);
        if (ids.length) {
          fnoService.getAssessmentsBatch(ids)
            .then(map => setFnoAssessments(map))
            .catch(() => {});
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAlerts();
  };

  const handleAcknowledgeAlert = async (alertId: number) => {
    try {
      await tradingService.acknowledgeAlert(alertId);
      fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to acknowledge alert');
    }
  };

  const handleParkAlert = async (alertId: number) => {
    try {
      await tradingService.parkAlert(alertId);
      fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to park alert');
    }
  };

  const toggleSelectAlert = (id: number) => {
    setSelectedAlerts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAlerts.size === alerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(alerts.map(a => a.id)));
    }
  };

  const handleBulkUpdate = async () => {
    try {
      setBulkUpdating(true);
      await api.put('/alerts/bulk-update', {
        alertIds: Array.from(selectedAlerts),
        status: bulkTargetStatus
      });
      setSelectedAlerts(new Set());
      setBulkStatusDialogOpen(false);
      fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to bulk update alerts');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAlerts.size === 0) return;
    if (!window.confirm(`Delete ${selectedAlerts.size} selected alert(s)? This cannot be undone.`)) return;
    try {
      await api.delete('/alerts/bulk', { data: { alertIds: Array.from(selectedAlerts) } });
      setSelectedAlerts(new Set());
      fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete alerts');
    }
  };

  const handleTestOrderPlacement = async (alertId: number) => {
    try {
      setTestingOrderPlacement(alertId);
      setError(null);

      const response = await tradingService.testPlaceOrderFromAlert(alertId);
      setTestOrderResult(response.data);
      setOpenTestResultDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to test order placement');
      setTestOrderResult(err.response?.data);
      setOpenTestResultDialog(true);
    } finally {
      setTestingOrderPlacement(null);
    }
  };

  const openEditPanel = (alert: Alert) => {
    setEditingAlertId(alert.id);
    // Pre-populate from saved custom prices, then fall back to parsedData from TradingView
    const pd = (alert as any).parsedData || {};
    const inst = (alert as any).instrument;

    const entryPrice = alert.customEntryPrice ?? (alert as any).currentPrice ?? pd.entryLevel ?? pd.price ?? 0;
    setCustomEntry(entryPrice ? String(entryPrice) : '');
    setCustomSL(alert.customSL != null ? String(alert.customSL) : (pd.isl ? String(pd.isl) : ''));
    setCustomTarget(alert.customTarget != null ? String(alert.customTarget) : (pd.target ? String(pd.target) : ''));
    setCustomRenotify(alert.renotifyAt != null ? String(alert.renotifyAt) : '');

    // Auto-calculate qty: prefer parsedData.quantity, then compute from investment amounts
    let autoQty = '';
    if (pd.quantity) {
      autoQty = String(pd.quantity);
    } else if (entryPrice > 0 && inst) {
      const seg = (inst.tradeSegment || '').toUpperCase();
      const mode = (inst.tradeMode || '').toUpperCase();
      const exchange = (inst.exchange || '').toUpperCase();
      const instType = (inst.instrumentType || inst.type || '').toUpperCase();
      const lotSize = Number(inst.lotSize || 1);

      // "Size orders as": explicit Equity/Futures, else Auto (server rule, utils/sizeOrdersAs).
      const isFutures = effectiveSegment(exchange, instType, seg) === 'FUTURES' || ['CE','PE'].includes(instType);
      const isEquity = !isFutures;

      if (isFutures && inst.futInvestmentAmount) {
        const computed = Math.max(1, Math.floor(inst.futInvestmentAmount / (entryPrice * lotSize)));
        autoQty = String(computed);
      } else if (isEquity && inst.eqInvestmentAmount) {
        const computed = Math.max(1, Math.floor(inst.eqInvestmentAmount / entryPrice));
        autoQty = String(computed);
      } else if (inst.eqInvestmentAmount && entryPrice > 0) {
        // Fallback for instruments without segment configured
        const computed = Math.max(1, Math.floor(inst.eqInvestmentAmount / entryPrice));
        autoQty = String(computed);
      }
    }
    setCustomQuantity(autoQty);
    setCustomOrderType(alert.customEntryPrice || pd.price ? 'LIMIT' : 'MARKET');
  };

  const closeEditPanel = () => {
    setEditingAlertId(null);
    setCustomEntry('');
    setCustomSL('');
    setCustomTarget('');
    setCustomRenotify('');
    setCustomQuantity('');
    setCustomOrderType('LIMIT');
  };

  const handlePlaceWithCustom = async (alertId: number) => {
    try {
      setPlacingOrder(true);
      setError(null);
      const body: any = { orderType: customOrderType };
      if (customEntry) body.customEntryPrice = parseFloat(customEntry);
      if (customSL) body.customSL = parseFloat(customSL);
      if (customTarget) body.customTarget = parseFloat(customTarget);
      if (customQuantity) body.customQuantity = parseFloat(customQuantity);
      const response = await api.post(`/alerts/${alertId}/execute`, body);
      setTestOrderResult(response.data);
      setOpenTestResultDialog(true);
      closeEditPanel();
      fetchAlerts();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to execute order');
      setTestOrderResult(err.response?.data);
      setOpenTestResultDialog(true);
    } finally {
      setPlacingOrder(false);
    }
  };

  // Live type-ahead search for ticker autocomplete in test webhook dialog
  const searchTickerOptions = (input: string) => {
    const term = input.trim();
    if (!term) { setTickerOptions([]); return; }
    setTickerSearchLoading(true);
    api.get('/instruments', { params: { search: term.toUpperCase(), pageSize: 20 } }).then((r: any) => {
      const items: any[] = r.data?.data?.instruments || r.data?.instruments || [];
      setTickerOptions(items.map((inst: any) => ({ symbol: inst.symbol, exchange: inst.exchange || '', name: inst.name || '' })));
    }).catch(() => {}).finally(() => setTickerSearchLoading(false));
  };

  const handleSendTestWebhook = async () => {
    try {
      setTestWebhookSending(true);
      const body: any = {
        symbol: testWebhookPayload.ticker.toUpperCase(),
        action: testWebhookPayload.action,
        price: parseFloat(testWebhookPayload.price) || 0,
        strategy: testWebhookPayload.strategy,
        timeframe: testWebhookPayload.timeframe,
        confirmed: testWebhookPayload.confirmed
      };
      if (testWebhookPayload.isl) body.isl = parseFloat(testWebhookPayload.isl);
      if (testWebhookPayload.target) body.target = parseFloat(testWebhookPayload.target);
      if (testWebhookPayload.entryLevel) body.entryLevel = parseFloat(testWebhookPayload.entryLevel);
      const response = await api.post('/alerts/test-webhook', body);
      setTestOrderResult(response.data);
      setOpenTestResultDialog(true);
      setTestWebhookOpen(false);
      fetchAlerts();
    } catch (err: any) {
      setTestOrderResult(err.response?.data || { error: err.message });
      setOpenTestResultDialog(true);
    } finally {
      setTestWebhookSending(false);
    }
  };

  const formatDateTime = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority: AlertPriority): 'error' | 'warning' | 'info' => {
    switch (priority) {
      case AlertPriority.HIGH:
        return 'error';
      case AlertPriority.MEDIUM:
        return 'warning';
      case AlertPriority.LOW:
        return 'info';
      default:
        return 'info';
    }
  };

  // Issue 1: Display "PROCESSED" instead of "ACKNOWLEDGED" everywhere in UI
  const getStatusLabel = (status: string): string => status === 'ACKNOWLEDGED' ? 'PROCESSED' : status;

  const getStatusColor = (status: AlertStatus): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case AlertStatus.TRIGGERED:
        return 'success';
      case AlertStatus.EXPIRED:
        return 'error';
      case AlertStatus.ACKNOWLEDGED:
        return 'default';
      case AlertStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case AlertType.PRICE:
        return <NotificationsActiveIcon />;
      case AlertType.RISK:
        return <ErrorIcon />;
      case AlertType.SYSTEM:
        return <InfoIcon />;
      default:
        return <WarningIcon />;
    }
  };

  // Auto-trade skip reasons → short chip label + explanation. Returns null when
  // the signal was not auto-skipped.
  const autoSkipMeta = (reason?: string | null): { label: string; tip: string } | null => {
    switch (reason) {
      case 'AUTO_LONG_OFF':  return { label: 'AL OFF', tip: 'Auto-trade skipped — Auto Long is OFF for this symbol. Enable it on the Instruments page to allow auto long entries.' };
      case 'AUTO_SHORT_OFF': return { label: 'AS OFF', tip: 'Auto-trade skipped — Auto Short is OFF for this symbol. Enable it on the Instruments page to allow auto short entries.' };
      case 'POSITION_OPEN':  return { label: 'POSITION OPEN', tip: 'Auto-entry skipped — a position is already open for this symbol, so no new/stacked entry was taken (applies across all scheduled-scan timeframes).' };
      case 'RESERVE':        return { label: 'RESERVE', tip: 'Auto-entry skipped — placing it would breach your Portfolio Reserve %. Free up margin or lower the reserve (Settings).' };
      default:               return reason ? { label: String(reason), tip: `Auto-trade skipped: ${reason}` } : null;
    }
  };

  // Normalise RockBand's exit verbs (SELL_FLAT closes a long, COVER_FLAT closes
  // a short) onto the same 4-colour scheme so entries vs exits read at a glance:
  //   BUY (entry long) → green · SHORT (entry short) → amber
  //   SELL/SELL_FLAT (exit long) → red · COVER/COVER_FLAT (exit short) → blue
  const normAction = (action?: string): 'BUY' | 'SELL' | 'SHORT' | 'COVER' | '' => {
    const a = action?.toUpperCase() || '';
    if (a === 'BUY') return 'BUY';
    if (a === 'SHORT') return 'SHORT';
    if (a === 'SELL' || a === 'SELL_FLAT') return 'SELL';
    if (a === 'COVER' || a === 'COVER_FLAT') return 'COVER';
    return '';
  };

  const getActionColor = (action?: string): string => {
    switch (normAction(action)) {
      case 'BUY':   return C.bull;
      case 'SELL':  return C.bear;
      case 'SHORT': return C.warn;
      case 'COVER': return C.neutral;
      default:      return C.accent;
    }
  };

  const getActionBgTint = (action?: string): string => {
    switch (normAction(action)) {
      case 'BUY':   return C.bullMuted;
      case 'SELL':  return C.bearMuted;
      case 'SHORT': return C.warnMuted;
      case 'COVER': return C.neutralMuted;
      default:      return C.accentMuted;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">Alerts</Typography>
          <Tooltip title={lifecycleExpanded ? 'Hide lifecycle guide' : 'Show alert lifecycle guide'}>
            <IconButton size="small" onClick={() => setLifecycleExpanded(v => !v)}>
              {lifecycleExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {selectedAlerts.size > 0 && (
            <>
              <Chip label={`${selectedAlerts.size} selected`} size="small" color="primary" />
              <Button
                size="small"
                variant="contained"
                startIcon={<DoneAllIcon />}
                onClick={() => setBulkStatusDialogOpen(true)}
              >
                Update Status
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected
              </Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedAlerts(new Set())}>
                Clear
              </Button>
            </>
          )}
          <Tooltip title={selectedAlerts.size === alerts.length ? 'Deselect all' : 'Select all'}>
            <IconButton onClick={toggleSelectAll} size="small">
              <Checkbox
                checked={alerts.length > 0 && selectedAlerts.size === alerts.length}
                indeterminate={selectedAlerts.size > 0 && selectedAlerts.size < alerts.length}
                size="small"
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Opens the Alerts section of the App Guide in a new tab (action codes, CONFIRMED=Y/N, dedup, renotify, bulk actions, test webhook)">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => window.open('/app-guide#action-codes', '_blank', 'noopener')}
              sx={{ fontSize: CH_TYPE.small }}
            >
              📖 Setup Guide
            </Button>
          </Tooltip>
          <Tooltip title="Send a Test Alert (simulates a TradingView webhook)">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ScienceIcon />}
              onClick={() => setTestWebhookOpen(true)}
              sx={{ fontSize: CH_TYPE.small }}
            >
              Test Alert
            </Button>
          </Tooltip>
          <Tooltip title="Create a manual price alert — notify when a symbol crosses a level">
            <Button size="small" variant="outlined" startIcon={<AddAlertIcon />} onClick={() => setPriceAlertOpen(true)} sx={{ fontSize: CH_TYPE.small }}>
              Price alert
            </Button>
          </Tooltip>
          <Tooltip title={density === 'cards' ? 'Switch to compact rows' : 'Switch to full cards'}>
            <IconButton onClick={toggleDensity} size="small">
              {density === 'cards' ? <ViewHeadlineOutlinedIcon fontSize="small" /> : <ViewAgendaOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <PriceAlertDialog open={priceAlertOpen} onClose={() => setPriceAlertOpen(false)} onCreated={handleRefresh} />

      {/* Issue 6: Alert Lifecycle Guide */}
      <Collapse in={lifecycleExpanded}>
        <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: C.neutralMuted, border: `1px solid ${C.neutralBorder}` }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: C.neutral }}>
            Alert Lifecycle
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
            <Chip label="PENDING" color="warning" size="small" />
            <Typography variant="caption" sx={{ mx: 0.5 }}>→</Typography>
            <Chip label="TRIGGERED" color="success" size="small" />
            <Typography variant="caption" sx={{ mx: 0.5 }}>→</Typography>
            <Chip label="PROCESSED (Parked)" size="small" />
            <Typography variant="caption" sx={{ mx: 0.5 }}>→</Typography>
            <Chip label="EXPIRED / DISMISSED" color="error" size="small" />
          </Box>
          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
              <Chip label="PENDING" color="warning" size="small" sx={{ minWidth: 90, justifyContent: 'center' }} />
              <span>Alert condition set but not yet met. TradingView webhook received and waiting for price confirmation.</span>
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
              <Chip label="TRIGGERED" color="success" size="small" sx={{ minWidth: 90, justifyContent: 'center' }} />
              <span>Condition met — TradingView fired the webhook (bar close or intrabar). Alert appears in Dashboard and is ready to execute.</span>
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
              <Chip label="PARKED/PROCESSED" size="small" sx={{ minWidth: 90, justifyContent: 'center' }} />
              <span>Parked = deferred for later review (appears in Dashboard Parked Alerts, still executable). Processed = marked done without placing an order.</span>
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
              <Chip label="PROCESSED" size="small" sx={{ minWidth: 90, justifyContent: 'center' }} />
              <span>Processed — either an order was placed, or it was marked done. No further action needed.</span>
            </Typography>
            <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
              <Chip label="EXPIRED" color="error" size="small" sx={{ minWidth: 90, justifyContent: 'center' }} />
              <span>Alert window passed without action, or manually dismissed. Retained for history.</span>
            </Typography>
          </Stack>
        </Box>
      </Collapse>

      {/* Filters */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Search Symbol"
            value={symbolSearch}
            onChange={(e) => setSymbolSearch(e.target.value.toUpperCase())}
            placeholder="e.g. BTCUSD"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="all">All actions</MenuItem>
              <MenuItem value="BUY">BUY</MenuItem>
              <MenuItem value="SHORT">SHORT</MenuItem>
              <MenuItem value="SELL">SELL (close long)</MenuItem>
              <MenuItem value="COVER">COVER (close short)</MenuItem>
              <MenuItem value="SELL_FLAT">SELL_FLAT</MenuItem>
              <MenuItem value="COVER_FLAT">COVER_FLAT</MenuItem>
              <MenuItem value="NOTIFY">NOTIFY (info-only)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Origin</InputLabel>
            <Select
              value={originFilter}
              label="Origin"
              onChange={(e) => setOriginFilter(e.target.value)}
            >
              <MenuItem value="all">All origins</MenuItem>
              <MenuItem value="TV">TradingView webhook</MenuItem>
              <MenuItem value="SCAN">In-house Scheduled Scan</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Strategy"
            placeholder="Type to filter…"
            value={strategyFilter}
            onChange={(e) => setStrategyFilter(e.target.value)}
            InputProps={{
              endAdornment: strategyFilter ? (
                <InputAdornment position="end">
                  <Tooltip arrow title="Clear strategy filter">
                    <IconButton size="small" edge="end" onClick={() => setStrategyFilter('')} sx={{ mr: -0.5 }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Strength</InputLabel>
            <Select
              value={strengthFilter}
              label="Strength"
              onChange={(e) => setStrengthFilter(e.target.value)}
            >
              <MenuItem value="all">All strengths</MenuItem>
              <MenuItem value="H">High</MenuItem>
              <MenuItem value="M">Medium</MenuItem>
              <MenuItem value="L">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Date (IST)"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={dateFilter ? 'Showing alerts on this day' : ''}
            InputProps={{
              endAdornment: dateFilter ? (
                <InputAdornment position="end">
                  <Tooltip arrow title="Clear date filter">
                    <IconButton size="small" edge="end" onClick={() => setDateFilter('')} sx={{ mr: -0.5 }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="TRIGGERED">Triggered</MenuItem>
              <MenuItem value="ACKNOWLEDGED">Processed / Parked</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip arrow title="How many past days of alerts to load from the server. Higher = more history (no 20-row cap). The single-date picker above overrides this for one specific day.">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5, cursor: 'default' }}>Load:</Typography>
            </Tooltip>
            {([
              { v: null, l: 'All' },
              { v: 1, l: '1d' },
              { v: 2, l: '2d' },
              { v: 3, l: '3d' },
              { v: 5, l: '5d' },
              { v: 7, l: '7d' },
              { v: 10, l: '10d' },
              { v: 15, l: '15d' },
            ] as { v: number | null; l: string }[]).map(opt => (
              <Chip
                key={opt.l}
                label={opt.l}
                size="small"
                onClick={() => setDaysFilter(opt.v)}
                variant={daysFilter === opt.v ? 'filled' : 'outlined'}
                color={daysFilter === opt.v ? 'primary' : 'default'}
                sx={{ height: 24, fontSize: CH_TYPE.small, fontWeight: 600 }}
              />
            ))}
            <Typography variant="caption" sx={{ color: 'text.disabled', ml: 1 }}>
              {alerts.length} loaded
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}>Interval:</Typography>
            {([
              { v: 'all', l: 'All' },
              { v: '1', l: '1m' },
              { v: '5', l: '5m' },
              { v: '15', l: '15m' },
              { v: '60', l: '1h' },
              { v: '240', l: '4h' },
              { v: 'D', l: 'D' },
              { v: 'W', l: 'W' },
            ]).map(opt => (
              <Chip
                key={opt.v}
                label={opt.l}
                size="small"
                onClick={() => setIntervalFilter(opt.v)}
                variant={intervalFilter === opt.v ? 'filled' : 'outlined'}
                color={intervalFilter === opt.v ? 'primary' : 'default'}
                sx={{ height: 24, fontSize: CH_TYPE.small, fontWeight: 600 }}
              />
            ))}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="When ON, the filters above apply to the latest alert per symbol only (older alerts for the same symbol are hidden)." arrow>
              <Chip
                label="Latest per symbol"
                size="small"
                onClick={() => setLatestPerSymbol(v => !v)}
                variant={latestPerSymbol ? 'filled' : 'outlined'}
                color={latestPerSymbol ? 'secondary' : 'default'}
                sx={{ height: 24, fontSize: CH_TYPE.small, fontWeight: 600 }}
              />
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {loading && alerts.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (() => {
        // Filter chain is computed once at component scope (filteredAlerts memo
        // above) so the footer count matches exactly what's rendered here.

        // R83 — relative-time helper for the compact row's "Triggered" column.
        // Compact strings ("3m ago", "2h ago") read at a glance vs. a full
        // date/time string. Falls back to the absolute date for anything ≥ 7d.
        const fmtRelTime = (iso?: string | Date | null): string => {
          if (!iso) return '—';
          const t = (iso instanceof Date) ? iso.getTime() : new Date(iso).getTime();
          if (!Number.isFinite(t)) return '—';
          const diff = Math.max(0, Date.now() - t);
          if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
          if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
          if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
          if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
          return new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
        };

        if (filteredAlerts.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No alerts found</Typography>
            </Box>
          );
        }
        return (
        density === 'compact' ? (
        /* ════════════════════════════════════════════════════════════════
           R83 — Compact row layout. One alert = one ~48-52 px-tall row.
           Tradition is to read 8-12 alerts at a glance. Bulk operations
           via the row checkbox; one-click Execute on the right; click
           the chevron (or anywhere on the row body) to expand inline
           full detail.
           ──────────────────────────────────────────────────────────── */
        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1,
          bgcolor: 'background.paper', overflow: 'hidden',
        }}>
          {filteredAlerts.map((alert, idx) => {
            const pd = (alert as any).parsedData || {};
            const action = pd.action as string | undefined;
            const sideColor = getActionColor(action);
            const rowTint = getActionBgTint(action);   // full-row tint for at-a-glance type recognition
            const isBuy = action === 'BUY' || action === 'COVER' || action === 'COVER_FLAT';
            const symbol = alert.symbol || pd.ticker || pd.symbol || '—';
            /* Capped by tick size, not the raw stored 8 — see utils/decimalPrecision.ts. */
            const dp = resolveDecimals((alert as any).instrument);
            const tf = pd.timeframe;
            const stratRaw: string = (pd.strategy || pd.signalSource || '').toUpperCase();
            const stratShort = stratRaw.includes('ROCKBAND') ? 'RB'
              : stratRaw.includes('DAILYBLAST') ? 'DB'
              : stratRaw.includes('SNIPER') ? 'SNP'
              : stratRaw.length > 6 ? stratRaw.slice(0, 6) : stratRaw;
            // Strength: RockBand sets a string ('HIGH'|'MEDIUM'|'LOW') on
            // alert.strength; TradingView alerts use a numeric pd.signalStrength
            // (3/2/1). Support both so the chip is never blank for RB alerts.
            const strength: 'H' | 'M' | 'L' | null = (() => {
              const raw = (alert as any).strength ?? pd.strength ?? pd.signalStrength;
              if (raw == null || raw === '') return null;
              if (typeof raw === 'number') return raw >= 3 ? 'H' : raw >= 2 ? 'M' : 'L';
              const u = String(raw).toUpperCase();
              if (u.startsWith('H')) return 'H';
              if (u.startsWith('M')) return 'M';
              if (u.startsWith('L')) return 'L';
              return null;
            })();
            const strengthColor = strength === 'H' ? C.bull : strength === 'M' ? C.warn : C.bear;
            const confirmedRaw = pd.confirmed;
            const isOpen = expandedRowId === alert.id;
            const isActionable = !alert.isAcknowledged && alert.status === AlertStatus.TRIGGERED;

            // R118 — Alert Origin chip: TradingView webhook payloads land in
            // alert.rawAlert (raw JSON); in-house strategy scans never set it.
            // This is the most reliable disambiguator (parsedData fields like
            // signalSource can be set by both paths).
            const isTradingViewOrigin = !!alert.rawAlert;
            const originLabel = isTradingViewOrigin ? 'TV' : 'SCAN';
            const originFullName = isTradingViewOrigin ? 'TradingView webhook' : 'In-house Scheduled Scan';
            const originColor = isTradingViewOrigin ? C.accent : C.neutral;
            const originBgMuted = isTradingViewOrigin ? C.accentMuted : C.neutralMuted;
            const exch = (alert as any).instrument?.exchange || '';

            const entry = alert.customEntryPrice || pd.price || alert.triggerPrice || pd.entryLevel;
            const sl    = alert.customSL    || pd.isl;
            const tgt   = alert.customTarget|| pd.target;
            const ltp   = alert.currentPrice;

            const ltpChgPct = (() => {
              const e = Number(entry);
              const l = Number(ltp);
              if (!Number.isFinite(e) || !Number.isFinite(l) || e === 0) return null;
              const pct = ((l - e) / e) * 100 * (isBuy ? 1 : -1);
              return pct;
            })();

            return (
              <Box key={alert.id}>
                {/* main row */}
                <Box
                  onClick={() => setExpandedRowId(isOpen ? null : alert.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1, py: 0.75, minHeight: 48,
                    borderLeft: '4px solid', borderColor: sideColor,
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderTopColor: 'divider',
                    // Full-row tint by action type (BUY=green, SHORT=amber,
                    // SELL=red, COVER=blue). Hover/open states are layered on top
                    // via an inset box-shadow so the type tint always shows.
                    bgcolor: rowTint,
                    boxShadow: isOpen ? `inset 0 0 0 9999px ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)'}` : 'none',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: `inset 0 0 0 9999px ${isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.028)'}` },
                    transition: 'box-shadow 0.12s',
                  }}
                >
                  {/* selection checkbox */}
                  <Checkbox
                    size="small"
                    checked={selectedAlerts.has(alert.id)}
                    onChange={() => toggleSelectAlert(alert.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.25 }}
                  />

                  {/* Side slot — always present, empty when the alert has no side, so every
                      row's symbol starts at the same x. */}
                  <Box sx={{ width: 62, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {action && (
                      <Chip
                        label={action}
                        size="small"
                        sx={{
                          height: 20, fontSize: CH_TYPE.micro, fontWeight: 800, letterSpacing: 0.3,
                          bgcolor: getActionBgTint(action), color: sideColor,
                          border: `1px solid ${sideColor}55`,
                          minWidth: 56,
                        }}
                      />
                    )}
                  </Box>

                  {/* symbol */}
                  <Typography noWrap sx={{
                    fontWeight: 800, fontSize: CH_TYPE.body,
                    fontFamily: 'ui-monospace, Menlo, Monaco, monospace',
                    width: 112, flexShrink: 0, color: 'text.primary',
                  }}>
                    {symbol}
                  </Typography>

                  {/* R118 — chart icon: opens this symbol+exchange in a new chart
                      tab, on the alert's own timeframe when it is identifiable.
                      Scanner alerts (ThetaMiner_Ultra / Bhairav_Scanner) carry a
                      syntheticId — the icon then opens THAT synthetic's chart. */}
                  {/* Chart-icon slot — fixed, so the meta chips after it line up whether
                      or not this alert can be charted. */}
                  <Box sx={{ width: 24, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  {(() => {
                    const synthId = ((alert as any).additionalData)?.syntheticId;
                    const synthName = ((alert as any).additionalData)?.synthetic;
                    if (synthId != null) {
                      return (
                        <Tooltip arrow title={`Open the ${synthName || 'synthetic'} chart this signal fired on${tf ? ` (${tf})` : ''}`}>
                          <IconButton size="small" sx={{ p: 0.2 }}
                            onClick={(e) => { e.stopPropagation(); openChartTab(synthName || symbol, 'SYNTH', { synthId: String(synthId) }); }}>
                            <CandlestickChartIcon sx={{ fontSize: 16, color: '#7e57c2' }} />
                          </IconButton>
                        </Tooltip>
                      );
                    }
                    if (!symbol || symbol === '—' || !exch) return null;
                    return (
                      <Tooltip arrow title={`Open chart for ${symbol} on ${exch}${tf ? ` (${tf})` : ''}`}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.2 }}
                          onClick={(e) => { e.stopPropagation(); openChartTab(symbol, exch, chartOpenQuery(tf, (alert.parsedData as any)?.strategy || (alert.parsedData as any)?.signalSource)); }}
                        >
                          <CandlestickChartIcon sx={{ fontSize: 16, color: C.neutral }} />
                        </IconButton>
                      </Tooltip>
                    );
                  })()}
                  </Box>

                  {/* Meta slot — origin / timeframe / strategy / strength / confirmed.
                      Fixed width so the price block always starts at the same x. */}
                  <Box sx={{ width: 250, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                  {/* R118 — Origin chip (TradingView webhook vs in-house Scheduled Scan) */}
                  <Tooltip arrow title={`Origin — ${originFullName}. ${isTradingViewOrigin ? 'Came in via the TradingView webhook URL configured in Settings.' : 'Generated server-side by the strategy scanner (no TradingView involvement).'}`}>
                    <Chip
                      label={originLabel}
                      size="small"
                      sx={{ height: 18, fontSize: CH_TYPE.micro, bgcolor: originBgMuted, color: originColor, fontWeight: 700 }}
                    />
                  </Tooltip>

                  {/* tf + strategy + strength chips */}
                  {tf && (
                    <Tooltip arrow title={`Origin interval — the chart timeframe this alert fired on (${tf} bars).`}>
                      <Chip label={tf} size="small" sx={{ height: 18, fontSize: CH_TYPE.micro, bgcolor: C.accentMuted, color: C.accent, fontWeight: 700 }} />
                    </Tooltip>
                  )}
                  {stratShort && (
                    <Tooltip arrow title={`Strategy — ${stratRaw || stratShort}. The strategy that generated this alert (RB = RockBand, DB = DailyBlast, SNP = Sniper).`}>
                      <Chip label={stratShort} size="small" sx={{ height: 18, fontSize: CH_TYPE.micro, bgcolor: C.neutralMuted, color: C.neutral, fontWeight: 700 }} />
                    </Tooltip>
                  )}
                  {strength && (
                    <Tooltip arrow title={`Signal strength — ${strength === 'H' ? 'HIGH' : strength === 'M' ? 'MEDIUM' : 'LOW'}. Higher = more sub-indicators in agreement at signal time.`}>
                      <Chip label={strength} size="small" sx={{ height: 18, width: 22, fontSize: CH_TYPE.micro, bgcolor: 'transparent', color: strengthColor, border: `1px solid ${strengthColor}`, fontWeight: 800 }} />
                    </Tooltip>
                  )}
                  {confirmedRaw && (
                    <Tooltip arrow title={confirmedRaw === 'Y' ? 'CONFIRMED=Y — fired AFTER bar close (will not repaint). Safe to act on.' : 'CONFIRMED=N — fired INTRABAR (may repaint if the bar closes differently). Wait for bar close or treat as a heads-up.'}>
                      <Chip
                        label={confirmedRaw === 'Y' ? '✓' : '~'}
                        size="small"
                        sx={{ height: 18, width: 22, fontSize: CH_TYPE.micro, fontWeight: 800,
                          bgcolor: confirmedRaw === 'Y' ? C.bullMuted : C.warnMuted,
                          color:   confirmedRaw === 'Y' ? C.bull      : C.warn }}
                      />
                    </Tooltip>
                  )}
                  {(alert as any).instrument?.wlLong && (
                    <Tooltip arrow title="On your Long Watchlist — symbol is flagged for long-side trades."><Chip label="WL:L" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: CH_TYPE.micro, height: 16 }} /></Tooltip>
                  )}
                  {(alert as any).instrument?.wlShort && (
                    <Tooltip arrow title="On your Short Watchlist — symbol is flagged for short-side trades."><Chip label="WL:S" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 16 }} /></Tooltip>
                  )}
                  {/* Auto-trade skip reason — why this signal did NOT auto-fire */}
                  {(() => { const sk = autoSkipMeta((alert as any).autoSkipReason); return sk && (
                    <Tooltip arrow title={sk.tip}>
                      <Chip label={`⊘ ${sk.label}`} size="small" sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 800, bgcolor: C.warnMuted, color: C.warn, border: `1px solid ${C.warnBorder}` }} />
                    </Tooltip>
                  ); })()}

                  </Box>

                  {/* Price lane — its own flexible column rather than pushed right by
                      `ml: auto`, which is what put a sparse row's prices in a
                      different place from a full row's. */}
                  <Box sx={{
                    display: 'flex', gap: 1.25, alignItems: 'center',
                    flex: 1, minWidth: 0, overflow: 'hidden',
                    fontFamily: 'ui-monospace, Menlo, Monaco, monospace', fontSize: CH_TYPE.small,
                    ...chNumeric,
                  }}>
                    {entry != null && (
                      <Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}>E </Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{formatPrice(Number(entry), dp)}</Box>
                      </Box>
                    )}
                    {sl != null && (
                      <Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}>SL </Box>
                        <Box component="span" sx={{ fontWeight: 700, color: C.bear }}>{formatPrice(Number(sl), dp)}</Box>
                      </Box>
                    )}
                    {tgt != null && (
                      <Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}>T </Box>
                        <Box component="span" sx={{ fontWeight: 700, color: C.bull }}>{formatPrice(Number(tgt), dp)}</Box>
                      </Box>
                    )}
                    {ltp != null && (
                      <Box>
                        <Box component="span" sx={{ color: 'text.secondary' }}>LTP </Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{formatPrice(Number(ltp), dp)}</Box>
                        {ltpChgPct != null && (
                          <Box component="span" sx={{ ml: 0.4, fontWeight: 700, color: ltpChgPct >= 0 ? C.bull : C.bear }}>
                            ({ltpChgPct >= 0 ? '+' : ''}{ltpChgPct.toFixed(2)}%)
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* time ago + status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: CH_TYPE.small, color: 'text.secondary', minWidth: 78, justifyContent: 'flex-end' }}>
                    {fmtRelTime(alert.triggeredAt || alert.createdAt)}
                  </Box>
                  <Chip
                    label={getStatusLabel(alert.status)}
                    color={getStatusColor(alert.status)}
                    size="small"
                    sx={{ height: 18, fontSize: CH_TYPE.micro, fontWeight: 700, minWidth: 84 }}
                  />

                  {/* one-click execute (only when actionable) */}
                  {isActionable && (
                    <Tooltip arrow title={(
                      <Box sx={{ fontSize: CH_TYPE.body, lineHeight: 1.5 }}>
                        <Box sx={{ fontWeight: 700, mb: 0.5 }}>One-click Execute</Box>
                        Places a live order via the standard execution pipeline. Total target qty = Investment Amount × Leverage ÷ (price × lotSize)
                        from the Instruments page, then divided into <b>Entry Split Count</b> legs (Settings → Trading). This click executes 1 leg;
                        further clicks execute later legs until the cap is reached. <b>Portfolio Reserve %</b> still gates: if free margin is below
                        the threshold, the order is rejected. Applies whether Auto-Trade is on or off.
                      </Box>
                    )}>
                      <IconButton
                        size="small"
                        disabled={executingId === alert.id}
                        onClick={(e) => { e.stopPropagation(); handleQuickExecute(alert); }}
                        sx={{ p: 0.4, color: C.bull, bgcolor: C.bullMuted, '&:hover': { bgcolor: C.bull, color: C.onSolid } }}
                      >
                        <BoltIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* expand chevron */}
                  <IconButton size="small" sx={{ p: 0.4 }} onClick={(e) => { e.stopPropagation(); setExpandedRowId(isOpen ? null : alert.id); }}>
                    {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>

                {/* expanded inline detail */}
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ px: 2, py: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.015)', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 1, color: 'text.secondary' }}>
                      {alert.message}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: '0.78rem', mb: 1.5 }}>
                      <Box><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{formatDateTime(alert.createdAt)}</Typography></Box>
                      {alert.triggeredAt && (<Box><Typography variant="caption" color="text.secondary">Triggered</Typography><Typography variant="body2">{formatDateTime(alert.triggeredAt)}</Typography></Box>)}
                      {pd.strategy && (<Box><Typography variant="caption" color="text.secondary">Strategy</Typography><Typography variant="body2">{pd.strategy}</Typography></Box>)}
                      {pd.exitReason && (<Box><Typography variant="caption" color="text.secondary">Exit reason</Typography><Typography variant="body2">{pd.exitReason.replace(/_/g, ' ')}</Typography></Box>)}
                      {strength && (<Box><Typography variant="caption" color="text.secondary">Strength</Typography><Typography variant="body2" sx={{ color: strengthColor, fontWeight: 700 }}>{strength === 'H' ? 'HIGH' : strength === 'M' ? 'MEDIUM' : 'LOW'}{pd.signalStrength != null ? ` (${pd.signalStrength}/3)` : ''}</Typography></Box>)}
                      {pd.confirmed && (<Box><Typography variant="caption" color="text.secondary">Confirmed</Typography><Typography variant="body2" sx={{ color: pd.confirmed === 'Y' ? C.bull : C.warn }}>{pd.confirmed === 'Y' ? 'Yes (Bar Close)' : 'No (Intrabar)'}</Typography></Box>)}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      <FnoScoreBadge
                        alertId={alert.id}
                        symbol={(alert as any).instrument?.symbol || pd.symbol || ''}
                        direction={action === 'SHORT' ? 'SHORT' : 'LONG'}
                        assessment={fnoAssessments[alert.id]}
                      />
                      {(alert as any).renotifyAfterTime && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                          <RenotifyCountdown target={(alert as any).renotifyAfterTime} color={C.accent} />
                        </Box>
                      )}
                      {alert.status === AlertStatus.TRIGGERED && !alert.isAcknowledged && (
                        <Tooltip arrow title="Process — mark this alert as acknowledged WITHOUT placing an order. Use when you've handled it manually elsewhere or want to dismiss it.">
                          <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => handleAcknowledgeAlert(alert.id)} color="primary">Process</Button>
                        </Tooltip>
                      )}
                      {!alert.isAcknowledged && (
                        <Tooltip arrow title="Park — move this alert to the Parked Alerts bucket for later review. You can ReNotify it by time or price-condition (see Setup Guide → Renotify rules).">
                          <Button size="small" variant="outlined" startIcon={<PauseCircleOutlineIcon />} onClick={() => handleParkAlert(alert.id)} sx={{ color: C.warn, borderColor: C.warnBorder, '&:hover': { borderColor: C.warn, bgcolor: C.warnMuted } }}>Park</Button>
                        </Tooltip>
                      )}
                      {!alert.isAcknowledged && editingAlertId !== alert.id && (
                        <Tooltip arrow title="Edit — open the inline order panel to adjust entry price, stop-loss, target, quantity and order type BEFORE placing.">
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditPanel(alert)}>Edit</Button>
                        </Tooltip>
                      )}
                      {!alert.isAcknowledged && (
                        <Tooltip arrow title={(
                          <Box sx={{ fontSize: CH_TYPE.body, lineHeight: 1.5 }}>
                            <Box sx={{ fontWeight: 700, mb: 0.5 }}>Execute</Box>
                            Opens the order panel for review then places a live order. Total target qty = Investment Amount × Leverage ÷ (price × lotSize)
                            from the Instruments page, divided into <b>Entry Split Count</b> legs (Settings → Trading). This click executes 1 leg;
                            you can click again to execute later legs until the cap is reached. <b>Portfolio Reserve %</b> still gates: if free margin is
                            below the threshold the order is rejected. Applies whether Auto-Trade is on or off.
                          </Box>
                        )}>
                          <Button size="small" variant="contained" startIcon={<ScienceIcon />} onClick={() => openEditPanel(alert)} color="success">Execute</Button>
                        </Tooltip>
                      )}
                    </Box>
                    {/* Custom edit panel — same inline form as cards mode */}
                    <Collapse in={editingAlertId === alert.id}>
                      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: `1px dashed ${C.warnBorder}`, bgcolor: C.warnMuted }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          Order Parameters — review and adjust before placing
                        </Typography>
                        {(() => {
                          const inst = (alert as any).instrument || {};
                          const ndp = Math.max(0, Math.min(8, Number.isFinite(Number(inst.decimalPrecision)) ? Number(inst.decimalPrecision) : 2));
                          const priceStep = Math.pow(10, -ndp);
                          const priceInputProps = { step: priceStep, min: 0 };
                          return (
                            <>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <FormControl size="small" sx={{ width: 110 }}>
                                  <InputLabel>Order Type</InputLabel>
                                  <Select value={customOrderType} label="Order Type" onChange={(e) => setCustomOrderType(e.target.value as 'LIMIT' | 'MARKET')}>
                                    <MenuItem value="LIMIT">Limit</MenuItem>
                                    <MenuItem value="MARKET">Market</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField size="small" label="Entry Price" value={customEntry} onChange={(e) => setCustomEntry(e.target.value)} type="number" sx={{ width: 120 }} placeholder={customOrderType === 'MARKET' ? 'Market' : 'Required'} disabled={customOrderType === 'MARKET'} inputProps={priceInputProps} />
                                <TextField size="small" label="Quantity (lots)" value={customQuantity} onChange={(e) => setCustomQuantity(e.target.value)} type="number" sx={{ width: 120 }} placeholder="Auto" inputProps={{ min: 1, step: 1 }} />
                                <TextField size="small" label="Stop Loss" value={customSL} onChange={(e) => setCustomSL(e.target.value)} type="number" sx={{ width: 120 }} inputProps={priceInputProps} />
                                <TextField size="small" label="Target" value={customTarget} onChange={(e) => setCustomTarget(e.target.value)} type="number" sx={{ width: 120 }} inputProps={priceInputProps} />
                                <TextField size="small" label="Re-Notify At" value={customRenotify} onChange={(e) => setCustomRenotify(e.target.value)} type="number" sx={{ width: 120 }} inputProps={priceInputProps} />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained" color="success" startIcon={placingOrder ? <CircularProgress size={14} /> : <ScienceIcon />} onClick={() => handlePlaceWithCustom(alert.id)} disabled={placingOrder}>
                                  {placingOrder ? 'Placing...' : 'Place Order'}
                                </Button>
                                <Button size="small" onClick={closeEditPanel} disabled={placingOrder}>Cancel</Button>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    </Collapse>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
        ) : (
        /* ────────────── original "cards" detailed layout ────────────── */
        <Stack spacing={2}>
          {filteredAlerts.map((alert) => (
            <Card
              key={alert.id}
              sx={{
                borderLeft: 6,
                borderColor: getActionColor(alert.parsedData?.action),
                backgroundColor: getActionBgTint(alert.parsedData?.action),
                '&:hover': { boxShadow: 3 }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selectedAlerts.has(alert.id)}
                      onChange={() => toggleSelectAlert(alert.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {getAlertIcon(alert.type)}
                    <Typography variant="h6">{alert.title}</Typography>
                    {(alert as any).instrument?.wlLong && <Chip label="WL:L" size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                    {(alert as any).instrument?.wlShort && <Chip label="WL:S" size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18 }} />}
                    {/* Auto-Trade was eligible but skipped — show why so the user
                        can take manual action (Auto-Long/Short OFF, a position
                        already open, or the portfolio reserve). */}
                    {(() => { const sk = autoSkipMeta((alert as any).autoSkipReason); return sk && (
                      <Tooltip arrow title={sk.tip}>
                        <Chip label={`AUTO SKIPPED · ${sk.label}`} size="small"
                          sx={{ bgcolor: C.warnMuted, color: C.warn, fontWeight: 800, fontSize: CH_TYPE.micro, height: 18, border: `1px solid ${C.warnBorder}` }} />
                      </Tooltip>
                    ); })()}
                    {/* Subsequent split-leg alert — a same-direction signal fired
                        while a same-direction trade is still open. Blue chip so
                        the user knows this is an additional split leg, not a
                        fresh entry. */}
                    {((alert as any).additionalData)?.isSplit && (
                      <Tooltip arrow title={`Subsequent split-leg alert: a same-direction signal fired while the trade is still open. Treat as an additional entry (split leg) under the existing open trade. (The autonomous path holds one position per symbol and does not stack; additional legs come from manual Entry-Splitting.)`}>
                        <Chip label={`SPLIT · ${((alert as any).additionalData)?.cond || ''}`} size="small"
                          sx={{ bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 800, fontSize: CH_TYPE.micro, height: 18, border: '1px solid #1976d2' }} />
                      </Tooltip>
                    )}
                    {/* F11 — Auto-trade execution failed (broker rejected the
                        order). Red chip so the user knows the alert is valid
                        but the trade didn't fire; click for the error reason. */}
                    {(alert as any).tradeError && (
                      <Tooltip arrow title={`Auto-trade FAILED: ${(alert as any).tradeError}. Common causes: insufficient margin, SL too close, broker session expired. Place manually via the Execute button or check Logs for the full stack.`}>
                        <Chip label="TRADE FAILED" size="small"
                          sx={{ bgcolor: '#ffcdd2', color: '#b71c1c', fontWeight: 800, fontSize: CH_TYPE.micro, height: 18, border: '1px solid #c62828' }} />
                      </Tooltip>
                    )}
                    {/* F25 — Telegram delivery outcome icon. Compact so it
                        sits alongside the other chips without dominating. */}
                    {(alert as any).telegramStatus && (() => {
                      const s = (alert as any).telegramStatus as 'sent' | 'failed' | 'skipped';
                      const cfg = s === 'sent'    ? { label: 'TG ✓', tip: 'Telegram delivered', bg: '#e8f5e9', fg: '#1b5e20', bd: '#43a047' }
                                : s === 'failed'  ? { label: 'TG ✗', tip: 'Telegram delivery FAILED — check Logs for the error (likely bot token / chat ID)', bg: '#ffebee', fg: '#b71c1c', bd: '#e53935' }
                                :                   { label: 'TG –', tip: 'Telegram skipped — Route to Telegram switch was OFF on this scan', bg: '#eceff1', fg: '#455a64', bd: '#90a4ae' };
                      return (
                        <Tooltip arrow title={cfg.tip}>
                          <Chip label={cfg.label} size="small"
                            sx={{ bgcolor: cfg.bg, color: cfg.fg, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18, border: `1px solid ${cfg.bd}` }} />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Issue 11: removed Priority chip; added Strategy + Interval */}
                    {alert.parsedData?.action && (
                      <Chip label={alert.parsedData.action.toUpperCase()} size="small"
                        sx={{ fontWeight: 'bold', color: getActionColor(alert.parsedData.action), bgcolor: getActionBgTint(alert.parsedData.action), border: `1px solid ${getActionColor(alert.parsedData.action)}55` }} />
                    )}
                    {((alert.parsedData as any)?.strategy || (alert.parsedData as any)?.signalSource) && (() => {
                      const src: string = ((alert.parsedData as any).strategy || (alert.parsedData as any).signalSource || '').toUpperCase();
                      const isRB = src.includes('ROCKBAND') || src.includes('RB');
                      return (
                        <Chip label={isRB ? '[RB]' : src} size="small"
                          sx={{
                            bgcolor: isRB ? C.bullMuted : C.neutralMuted,
                            color: isRB ? C.bull : C.neutral,
                            fontWeight: 700,
                            fontSize: CH_TYPE.micro,
                            border: `1px solid ${isRB ? C.bullBorder : C.neutralBorder}`
                          }} />
                      );
                    })()}
                    {(alert.parsedData as any)?.timeframe && (
                      <Chip label={(alert.parsedData as any).timeframe} size="small"
                        sx={{ bgcolor: C.accentMuted, color: C.accent, fontSize: CH_TYPE.micro }} />
                    )}
                    <Chip label={getStatusLabel(alert.status)} color={getStatusColor(alert.status)} size="small" />
                    {/* FNO Score Badge */}
                    <FnoScoreBadge
                      alertId={alert.id}
                      symbol={(alert as any).instrument?.symbol || (alert.parsedData as any)?.symbol || ''}
                      direction={(alert.parsedData as any)?.action === 'SHORT' ? 'SHORT' : 'LONG'}
                      assessment={fnoAssessments[alert.id]}
                    />
                    {/* One-click Execute chip — Phase A */}
                    {!alert.isAcknowledged && alert.status === AlertStatus.TRIGGERED && (
                      <Tooltip arrow title="One-click execute via the standard order pipeline">
                        <Chip
                          icon={<BoltIcon sx={{ fontSize: 14 }} />}
                          label={executingId === alert.id ? 'Executing…' : 'Execute'}
                          onClick={() => handleQuickExecute(alert)}
                          size="small"
                          disabled={executingId === alert.id}
                          sx={{
                            fontWeight: 700,
                            bgcolor: C.bull,
                            color: C.onSolid,
                            '& .MuiChip-icon': { color: C.onSolid },
                            '&:hover': { bgcolor: C.bull, filter: 'brightness(1.1)' },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {alert.message}
                </Typography>

                <Grid container spacing={2}>
                  {alert.symbol && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Symbol
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {alert.symbol}
                      </Typography>
                    </Grid>
                  )}
                  {alert.triggerPrice && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Trigger Price
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(parseFloat(alert.triggerPrice.toString()), resolveDecimalsFor((alert as any).instrument, parseFloat(alert.triggerPrice.toString())))}
                      </Typography>
                    </Grid>
                  )}
                  {alert.currentPrice && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Price
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(parseFloat(alert.currentPrice.toString()), resolveDecimalsFor((alert as any).instrument, parseFloat(alert.currentPrice.toString())))}
                      </Typography>
                    </Grid>
                  )}
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">{formatDateTime(alert.createdAt)}</Typography>
                  </Grid>
                  {alert.triggeredAt && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Triggered
                      </Typography>
                      <Typography variant="body2">{formatDateTime(alert.triggeredAt)}</Typography>
                    </Grid>
                  )}
                  {alert.parsedData?.signalStrength && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Signal Strength
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {[1, 2, 3].map((level) => (
                          <Box
                            key={level}
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: level <= alert.parsedData!.signalStrength!
                                ? alert.parsedData!.signalStrength! >= 3 ? C.bull : alert.parsedData!.signalStrength! >= 2 ? C.warn : C.bear
                                : C.dimMuted,
                            }}
                          />
                        ))}
                        <Typography variant="body2" fontWeight="medium" sx={{ ml: 0.5 }}>
                          {alert.parsedData.signalStrength}/3
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {/* Issue 11: Strategy and Interval (timeframe) */}
                  {((alert.parsedData as any)?.strategy || (alert.parsedData as any)?.signalSource) && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Strategy</Typography>
                      <Typography variant="body2" fontWeight="medium">{(alert.parsedData as any).strategy || (alert.parsedData as any).signalSource}</Typography>
                    </Grid>
                  )}
                  {(alert.parsedData as any)?.timeframe && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Interval</Typography>
                      <Typography variant="body2" fontWeight="medium">{(alert.parsedData as any).timeframe}</Typography>
                    </Grid>
                  )}
                  {alert.parsedData?.confirmed && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Confirmed
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" sx={{
                        color: alert.parsedData.confirmed === 'Y' ? 'success.main' : 'warning.main'
                      }}>
                        {alert.parsedData.confirmed === 'Y' ? 'Yes (Bar Close)' : 'No (Intrabar)'}
                      </Typography>
                    </Grid>
                  )}
                  {alert.parsedData?.exitReason && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Exit Reason
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {alert.parsedData.exitReason.replace(/_/g, ' ')}
                      </Typography>
                    </Grid>
                  )}
                  {alert.parsedData?.exitSL && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        SL at Exit
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(parseFloat(alert.parsedData.exitSL.toString()), resolveDecimalsFor((alert as any).instrument, parseFloat(alert.parsedData.exitSL.toString())))}
                      </Typography>
                    </Grid>
                  )}
                  {alert.allowMarketEntry && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Chip
                        label="Market Entry Allowed"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  {alert.isSimulation && (
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Chip label="Simulation" size="small" variant="outlined" />
                    </Grid>
                  )}
                </Grid>
              </CardContent>

              {/* Trade params summary — always visible when parsedData has price/SL/target */}
              {(() => {
                const pd = (alert as any).parsedData || {};
                const hasInfo = pd.price || pd.isl || pd.target || alert.customEntryPrice || alert.customSL || alert.customTarget;
                if (!hasInfo) return null;
                return (
                  <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: CH_TYPE.small, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                    {(alert.customEntryPrice || pd.price) && (
                      <Box><Typography variant="caption" color="text.secondary">Entry: </Typography>
                      <Typography variant="caption" fontWeight={700}>{formatPrice(Number(alert.customEntryPrice || pd.price), resolveDecimalsFor((alert as any).instrument, Number(alert.customEntryPrice || pd.price)))}</Typography></Box>
                    )}
                    {(alert.customSL || pd.isl) && (
                      <Box><Typography variant="caption" color="text.secondary">SL: </Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ color: C.bear }}>{formatPrice(Number(alert.customSL || pd.isl), resolveDecimalsFor((alert as any).instrument, Number(alert.customSL || pd.isl)))}</Typography></Box>
                    )}
                    {(alert.customTarget || pd.target) && (
                      <Box><Typography variant="caption" color="text.secondary">Target: </Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ color: C.bull }}>{formatPrice(Number(alert.customTarget || pd.target), resolveDecimalsFor((alert as any).instrument, Number(alert.customTarget || pd.target)))}</Typography></Box>
                    )}
                  </Box>
                );
              })()}

              {/* Inline custom price edit panel */}
              <Collapse in={editingAlertId === alert.id}>
                <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: `1px dashed ${C.warnBorder}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                    Order Parameters — review and adjust before placing
                  </Typography>
                  {(() => {
                    const inst = (alert as any).instrument || {};
                    const dp = Math.max(0, Math.min(8, Number.isFinite(Number(inst.decimalPrecision)) ? Number(inst.decimalPrecision) : 2));
                    const priceStep = Math.pow(10, -dp);
                    const priceInputProps = { step: priceStep, min: 0 };
                    return (
                      <>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <FormControl size="small" sx={{ width: 110 }}>
                            <InputLabel>Order Type</InputLabel>
                            <Select
                              value={customOrderType}
                              label="Order Type"
                              onChange={(e) => setCustomOrderType(e.target.value as 'LIMIT' | 'MARKET')}
                            >
                              <MenuItem value="LIMIT">Limit</MenuItem>
                              <MenuItem value="MARKET">Market</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Entry Price"
                            value={customEntry}
                            onChange={(e) => setCustomEntry(e.target.value)}
                            type="number"
                            sx={{ width: 120 }}
                            placeholder={customOrderType === 'MARKET' ? 'Market' : 'Required'}
                            disabled={customOrderType === 'MARKET'}
                            inputProps={priceInputProps}
                          />
                          <TextField
                            size="small"
                            label="Quantity (lots)"
                            value={customQuantity}
                            onChange={(e) => setCustomQuantity(e.target.value)}
                            type="number"
                            sx={{ width: 120 }}
                            placeholder="Auto"
                            inputProps={{ min: 1, step: 1 }}
                          />
                          <TextField
                            size="small"
                            label="Stop Loss"
                            value={customSL}
                            onChange={(e) => setCustomSL(e.target.value)}
                            type="number"
                            sx={{ width: 120 }}
                            inputProps={priceInputProps}
                          />
                          <TextField
                            size="small"
                            label="Target"
                            value={customTarget}
                            onChange={(e) => setCustomTarget(e.target.value)}
                            type="number"
                            sx={{ width: 120 }}
                            inputProps={priceInputProps}
                          />
                          <TextField
                            size="small"
                            label="Re-Notify At"
                            value={customRenotify}
                            onChange={(e) => setCustomRenotify(e.target.value)}
                            type="number"
                            sx={{ width: 120 }}
                            inputProps={priceInputProps}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={placingOrder ? <CircularProgress size={14} /> : <ScienceIcon />}
                            onClick={() => handlePlaceWithCustom(alert.id)}
                            disabled={placingOrder}
                          >
                            {placingOrder ? 'Placing...' : 'Place Order'}
                          </Button>
                          <Button size="small" onClick={closeEditPanel} disabled={placingOrder}>
                            Cancel
                          </Button>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              </Collapse>

              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2, gap: 0.5 }}>
                {(alert as any).renotifyAfterTime && (
                  <Tooltip title="Time-based re-notification countdown. The alert will fire a Telegram ping when this clock reaches zero." arrow>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1 }}>
                      <RenotifyCountdown target={(alert as any).renotifyAfterTime} color={C.accent} />
                    </Box>
                  </Tooltip>
                )}
                {alert.status === AlertStatus.TRIGGERED && !alert.isAcknowledged && (
                  <Tooltip title="Mark alert as processed — no order placed, just marking done">
                    <Button
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleAcknowledgeAlert(alert.id)}
                      color="primary"
                    >
                      Process
                    </Button>
                  </Tooltip>
                )}
                {!alert.isAcknowledged && (
                  <Tooltip title="Park this alert for later review">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PauseCircleOutlineIcon />}
                      onClick={() => handleParkAlert(alert.id)}
                      sx={{
                        color: C.warn,
                        borderColor: C.warnBorder,
                        '&:hover': {
                          borderColor: C.warn,
                          backgroundColor: C.warnMuted,
                        },
                      }}
                    >
                      Park
                    </Button>
                  </Tooltip>
                )}
                {!alert.isAcknowledged && editingAlertId !== alert.id && (
                  <Tooltip title="Set custom Entry / SL / Target / ReNotify">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => openEditPanel(alert)}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                )}
                {!alert.isAcknowledged && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ScienceIcon />}
                    onClick={() => openEditPanel(alert)}
                    color="success"
                  >
                    Execute
                  </Button>
                )}
              </CardActions>
            </Card>
          ))}
        </Stack>
        )
        );
      })()}

      {alerts.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
            {filteredAlerts.length !== alerts.length ? ` (of ${alerts.length} loaded)` : ''}
          </Typography>
        </Box>
      )}

      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkStatusDialogOpen} onClose={() => setBulkStatusDialogOpen(false)}>
        <DialogTitle>Update Status for {selectedAlerts.size} Alert{selectedAlerts.size !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>New Status</InputLabel>
            <Select value={bulkTargetStatus} label="New Status" onChange={(e) => setBulkTargetStatus(e.target.value)}>
              <MenuItem value="ACKNOWLEDGED">Process / Park</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStatusDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkUpdate} disabled={bulkUpdating}>
            {bulkUpdating ? 'Updating...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Alert Dialog — Simulates a TradingView webhook */}
      <Dialog open={testWebhookOpen} onClose={() => setTestWebhookOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Alert (Simulate TradingView Webhook)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This sends a webhook payload to the app in the same format as TradingView JSR CLR script alerts.
            The resulting alert will appear in the list and can be used to test execution, ReNotify, and custom fields.
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                freeSolo
                size="small"
                fullWidth
                options={tickerOptions}
                loading={tickerSearchLoading}
                value={testWebhookPayload.ticker}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.symbol}
                isOptionEqualToValue={(opt, val) => typeof val === 'string' ? opt.symbol === val : opt.symbol === val.symbol && opt.exchange === val.exchange}
                onChange={(_e, val) => {
                  if (!val) {
                    setSelectedTickerExchange('');
                  } else if (typeof val === 'string') {
                    setTestWebhookPayload(p => ({ ...p, ticker: val.toUpperCase() }));
                    setSelectedTickerExchange('');
                  } else {
                    setTestWebhookPayload(p => ({ ...p, ticker: val.symbol }));
                    setSelectedTickerExchange(val.exchange);
                  }
                }}
                onInputChange={(_e, value, reason) => {
                  const upper = value.toUpperCase();
                  setTestWebhookPayload(p => ({ ...p, ticker: upper }));
                  if (reason === 'input') searchTickerOptions(upper);
                  if (!value) setSelectedTickerExchange('');
                }}
                filterOptions={(x) => x}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={`${opt.symbol}-${opt.exchange}`}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{opt.symbol}</Typography>
                    <Chip
                      label={opt.exchange}
                      size="small"
                      sx={{ ml: 1, fontSize: CH_TYPE.small, height: 18,
                        bgcolor: opt.exchange === 'NSE' ? C.bullMuted : opt.exchange === 'BSE' ? C.warnMuted : C.accentMuted,
                        color: opt.exchange === 'NSE' ? C.bull : opt.exchange === 'BSE' ? C.warn : C.accent
                      }}
                    />
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ticker / Symbol"
                    helperText={selectedTickerExchange
                      ? `Instrument: ${testWebhookPayload.ticker} on ${selectedTickerExchange}`
                      : "Type to search — select to see exchange"}
                    FormHelperTextProps={{ sx: { color: selectedTickerExchange ? (selectedTickerExchange === 'NSE' ? 'success.main' : 'warning.main') : 'text.secondary' } }}
                  />
                )}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={testWebhookPayload.action}
                  label="Action"
                  onChange={e => setTestWebhookPayload(p => ({ ...p, action: e.target.value }))}
                >
                  <MenuItem value="BUY">BUY (Long Entry)</MenuItem>
                  <MenuItem value="SHORT">SHORT (Short Entry)</MenuItem>
                  <MenuItem value="SELL">SELL (Exit Long)</MenuItem>
                  <MenuItem value="COVER">COVER (Exit Short)</MenuItem>
                  <MenuItem value="NOTIFY">NOTIFY (Notification only)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Price (0 = market)"
                size="small"
                fullWidth
                type="number"
                value={testWebhookPayload.price}
                onChange={e => setTestWebhookPayload(p => ({ ...p, price: e.target.value }))}
              />
              <TextField
                label="ISL (Stop Loss)"
                size="small"
                fullWidth
                type="number"
                value={testWebhookPayload.isl}
                onChange={e => setTestWebhookPayload(p => ({ ...p, isl: e.target.value }))}
              />
              <TextField
                label="Target"
                size="small"
                fullWidth
                type="number"
                value={testWebhookPayload.target}
                onChange={e => setTestWebhookPayload(p => ({ ...p, target: e.target.value }))}
              />
              <TextField
                label="Entry Level"
                size="small"
                fullWidth
                type="number"
                value={testWebhookPayload.entryLevel}
                onChange={e => setTestWebhookPayload(p => ({ ...p, entryLevel: e.target.value }))}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Strategy</InputLabel>
                <Select
                  value={testWebhookPayload.strategy}
                  label="Strategy"
                  onChange={e => setTestWebhookPayload(p => ({ ...p, strategy: e.target.value }))}
                >
                  <MenuItem value="Sniper">Sniper</MenuItem>
                  <MenuItem value="Shaved Doji">Shaved Doji</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Timeframe"
                size="small"
                fullWidth
                value={testWebhookPayload.timeframe}
                onChange={e => setTestWebhookPayload(p => ({ ...p, timeframe: e.target.value }))}
                helperText="e.g. 5, 15, 60"
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Confirmed</InputLabel>
                <Select
                  value={testWebhookPayload.confirmed}
                  label="Confirmed"
                  onChange={e => setTestWebhookPayload(p => ({ ...p, confirmed: e.target.value }))}
                >
                  <MenuItem value="Y">Y (Bar Close)</MenuItem>
                  <MenuItem value="N">N (Intrabar)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: CH_TYPE.small }}>
            <strong>Payload preview:</strong><br />
            {JSON.stringify({
              ticker: testWebhookPayload.ticker,
              action: testWebhookPayload.action,
              price: parseFloat(testWebhookPayload.price) || 0,
              strategy: testWebhookPayload.strategy,
              isl: testWebhookPayload.isl ? parseFloat(testWebhookPayload.isl) : undefined,
              entryLevel: testWebhookPayload.entryLevel ? parseFloat(testWebhookPayload.entryLevel) : undefined,
              timeframe: testWebhookPayload.timeframe,
              confirmed: testWebhookPayload.confirmed,
              signalSource: 'JSR_CLR'
            }, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestWebhookOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={testWebhookSending ? <CircularProgress size={16} /> : <ScienceIcon />}
            onClick={handleSendTestWebhook}
            disabled={testWebhookSending || !testWebhookPayload.ticker || !testWebhookPayload.action}
          >
            {testWebhookSending ? 'Sending...' : 'Send Test Alert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Order Result Dialog */}
      <Dialog
        open={openTestResultDialog}
        onClose={() => {
          setOpenTestResultDialog(false);
          setTestOrderResult(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {testOrderResult?.success ? '✅ Test Order Result' : '❌ Test Order Failed'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {testOrderResult && (
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(testOrderResult, null, 2)}
                </pre>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenTestResultDialog(false);
              setTestOrderResult(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AlertsList;
